import {
  getAggregationDrawerOptions,
  getCurrencyDrawerOptions,
  getFeeModeDrawerOptions,
  getTierDrawerOptions,
  MarketFeeModeEnum,
  type Aggregation,
  type AllInTier,
  type ColumnDef,
  type Currency,
  type FeeTier,
  type InputsAndDropdownsDrawerOption,
  type PersistedDefaultTabProps,
  type Security,
  type TabProps,
} from '@talos/kyoko';
import type { ColDef } from 'ag-grid-community';
import { BOOLEAN_OPTIONS, EXCLUDE_EXCHANGE_FEES_GUIDE_TEXT } from '../utils';

export function getCustomerPricingTiersAllInTierPostPath(entity: AllInTier): string {
  return entity.Symbol == null ? `/all-in-tiers` : `/all-in-tiers/${encodeURIComponent(entity.Tier)}/symbols`;
}

export function getCustomerPricingTiersAllInTierPatchDeletePath(entity: AllInTier): string {
  return entity.Symbol == null
    ? `/all-in-tiers/${encodeURIComponent(entity.Tier)}`
    : `/all-in-tiers/${encodeURIComponent(entity.Tier)}/symbols/${entity.Symbol}`;
}

export function getCustomerPricingTiersFeeTierPostPath(entity: FeeTier): string {
  return entity.Symbol == null ? `/fee-tiers` : `/fee-tiers/${encodeURIComponent(entity.Tier)}/symbols`;
}

export function getCustomerPricingTiersFeeTierPatchDeletePath(entity: FeeTier): string {
  return entity.Symbol == null
    ? `/fee-tiers/${encodeURIComponent(entity.Tier)}`
    : `/fee-tiers/${encodeURIComponent(entity.Tier)}/symbols/${entity.Symbol}`;
}

const BPS_COLUMN_WIDTH = 120;
export const CUSTOMER_PRICING_TIERS_ALL_IN_TIER_COLUMNS: ColumnDef<AllInTier>[] = [
  { type: 'text', field: 'Tier', initialHide: true, initialSort: '+' },
  { type: 'security', field: 'Symbol', initialHide: true, initialSort: '+' },
  { type: 'bps', field: 'BidSpread', initialWidth: BPS_COLUMN_WIDTH },
  { type: 'bps', field: 'OfferSpread', initialWidth: BPS_COLUMN_WIDTH },
  { type: 'bps', field: 'SalesCommission', title: 'Commission', initialWidth: BPS_COLUMN_WIDTH },
  { type: 'pricingAggregation', field: 'PricingAggregation' },
  { type: 'bps', field: 'Fee', initialWidth: BPS_COLUMN_WIDTH },
  { type: 'feeMode', field: 'FeeMode' },
  { type: 'currency', field: 'FeeCurrency' },
  { type: 'text', field: 'QuoteTTL', title: 'Quote TTL', initialHide: true },
  { type: 'bps', field: 'AcceptPriceLeniency', initialHide: true },
  { type: 'text', field: 'ExcludeExchangeFees', initialHide: true },
];

export const CUSTOMER_PRICING_TIERS_FEE_TIERS_COLUMNS: ColumnDef<FeeTier>[] = [
  { type: 'text', field: 'Tier', initialHide: true, initialSort: '+' },
  { type: 'security', field: 'Symbol', initialHide: true, initialSort: '+' },
  { type: 'bps', field: 'Fee', initialWidth: BPS_COLUMN_WIDTH },
  { type: 'feeMode', field: 'FeeMode' },
  { type: 'currency', field: 'FeeCurrency' },
];

function getCustomerPricingTiersSharedAllInTierDrawerOptionsBeginning(
  pricingAggregations: Aggregation[]
): InputsAndDropdownsDrawerOption<AllInTier>[] {
  return [
    {
      field: 'BidSpread',
      type: 'inputNumeric',
      variant: 'bps',
      title: 'Bid Spread (BPS)',
    },
    {
      field: 'OfferSpread',
      type: 'inputNumeric',
      variant: 'bps',
      title: 'Offer Spread (BPS)',
    },
    {
      field: 'SalesCommission',
      type: 'inputNumeric',
      variant: 'bps',
      title: 'Commission (BPS)',
    },
    {
      field: 'PricingAggregation',
      type: 'dropdown',
      getDropdownOptions: () => getAggregationDrawerOptions(pricingAggregations),
      placeholder: 'Select Pricing Aggregation...',
    },
  ];
}

function getCustomerPricingTiersSharedAllInTierDrawerOptionsEnding(): InputsAndDropdownsDrawerOption<AllInTier>[] {
  return [
    {
      field: 'QuoteTTL',
      type: 'input',
      title: 'Quote TTL',
    },
    {
      field: 'AcceptPriceLeniency',
      type: 'inputNumeric',
      variant: 'bps',
      title: 'Price Leniency',
    },
    {
      field: 'ExcludeExchangeFees',
      type: 'dropdown',
      guideText: EXCLUDE_EXCHANGE_FEES_GUIDE_TEXT,
      getDropdownOptions: () => BOOLEAN_OPTIONS,
    },
  ];
}

function getCustomerPricingTiersSharedFeeTierDrawerOptions(
  feeModeOptions: MarketFeeModeEnum[],
  feeCurrencyOptions: Currency[]
): InputsAndDropdownsDrawerOption<FeeTier>[] {
  return [
    { field: 'Fee', type: 'inputNumeric', variant: 'bps', title: 'Fee (BPS)', getIsRequired: form => !!form.FeeMode },
    {
      field: 'FeeMode',
      type: 'dropdown',
      getDropdownOptions: () => getFeeModeDrawerOptions(feeModeOptions),
      getIsRequired: form => !!form.Fee,
      placeholder: 'Select Fee Mode...',
    },
    {
      field: 'FeeCurrency',
      type: 'dropdown',
      getDropdownOptions: () => getCurrencyDrawerOptions(feeCurrencyOptions),
      getIsHidden: form => form.FeeMode !== MarketFeeModeEnum.SpecificCurrency,
      getIsRequired: form => form.FeeMode === MarketFeeModeEnum.SpecificCurrency,
      placeholder: 'Select Fee Currency...',
    },
  ];
}

function getCustomerPricingTiersSharedAllInTierDrawerOptions(
  pricingAggregations: Aggregation[],
  feeModeOptions: MarketFeeModeEnum[],
  feeCurrencyOptions: Currency[]
): InputsAndDropdownsDrawerOption<AllInTier>[] {
  return [
    ...getCustomerPricingTiersSharedAllInTierDrawerOptionsBeginning(pricingAggregations),
    ...getCustomerPricingTiersSharedFeeTierDrawerOptions(feeModeOptions, feeCurrencyOptions),
    ...getCustomerPricingTiersSharedAllInTierDrawerOptionsEnding(),
  ];
}

export function getCustomerPricingTiersAllInTierDrawerOptions(
  pricingAggregations: Aggregation[],
  feeModeOptions: MarketFeeModeEnum[],
  feeCurrencyOptions: Currency[]
): InputsAndDropdownsDrawerOption<AllInTier>[] {
  return [
    {
      field: 'Tier',
      type: 'input',
      getIsRequired: () => true,
      disabledWhenEditing: true,
      title: 'Name',
    },
    { type: 'divider' },
    ...getCustomerPricingTiersSharedAllInTierDrawerOptions(pricingAggregations, feeModeOptions, feeCurrencyOptions),
  ];
}

export function getCustomerPricingTiersAllInTierOverridesDrawerOptions(
  pricingAggregations: Aggregation[],
  feeModeOptions: MarketFeeModeEnum[],
  feeCurrencyOptions: Currency[],
  allInTierGroups: AllInTier[],
  filteredSecurities: Security[]
): InputsAndDropdownsDrawerOption<AllInTier>[] {
  return [
    {
      field: 'Tier',
      type: 'dropdown',
      getIsRequired: () => true,
      disabledWhenEditing: true,
      title: 'Base Tier',
      getDropdownOptions: () => getTierDrawerOptions(allInTierGroups),
      placeholder: 'Select Base Tier...',
    },
    {
      field: 'Symbol',
      type: 'symbolSelector',
      disabledWhenEditing: true,
      disableKeyedOptions: true,
      getIsRequired: () => true,
      getAvailableSecurities: () => filteredSecurities,
    },
    { type: 'divider' },
    ...getCustomerPricingTiersSharedAllInTierDrawerOptions(pricingAggregations, feeModeOptions, feeCurrencyOptions),
  ];
}

export function getCustomerPricingTiersFeeTierDrawerOptions(
  feeModeOptions: MarketFeeModeEnum[],
  feeCurrencyOptions: Currency[]
): InputsAndDropdownsDrawerOption<FeeTier>[] {
  return [
    {
      field: 'Tier',
      type: 'input',
      getIsRequired: () => true,
      disabledWhenEditing: true,
      title: 'Name',
    },
    { type: 'divider' },
    ...getCustomerPricingTiersSharedFeeTierDrawerOptions(feeModeOptions, feeCurrencyOptions),
  ];
}

export function getCustomerPricingTiersFeeTierOverridesDrawerOptions(
  feeModeOptions: MarketFeeModeEnum[],
  feeCurrencyOptions: Currency[],
  feeTierGroups: FeeTier[],
  filteredSecurities: Security[]
): InputsAndDropdownsDrawerOption<FeeTier>[] {
  return [
    {
      field: 'Tier',
      type: 'dropdown',
      getIsRequired: () => true,
      disabledWhenEditing: true,
      title: 'Fee Tier',
      getDropdownOptions: () => getTierDrawerOptions(feeTierGroups),
      placeholder: 'Select Fee Tier...',
    },
    {
      field: 'Symbol',
      type: 'symbolSelector',
      disabledWhenEditing: true,
      disableKeyedOptions: true,
      getIsRequired: () => true,
      getAvailableSecurities: () => filteredSecurities,
    },
    { type: 'divider' },
    ...getCustomerPricingTiersSharedFeeTierDrawerOptions(feeModeOptions, feeCurrencyOptions),
  ];
}

export const CUSTOMER_PRICING_TIERS_GROUP_COLUMN_DEF: ColDef<AllInTier | FeeTier, string> = {
  headerName: 'Name',
  valueFormatter: ({ context, value }) => {
    const security = value ? context.current.securitiesBySymbol?.get(value) : null;
    return security?.DisplaySymbol ?? security?.Symbol ?? value ?? '';
  },
};

export type PricingTabType = 'Base Tiers' | 'Fee Tiers';

export interface CustomerPricingTabProps extends TabProps {
  tabType: PricingTabType;
}

export const CUSTOMER_PRICING_BASE_TIERS_TAB: PersistedDefaultTabProps<CustomerPricingTabProps> = {
  id: 'default-base-tiers',
  label: 'Base Tiers',
  closable: true,
  editable: true,
  reorderable: true,
  tabType: 'Base Tiers',
};

export const CUSTOMER_PRICING_FEE_TIERS_TAB: PersistedDefaultTabProps<CustomerPricingTabProps> = {
  id: 'default-fee-tiers',
  label: 'Fee Tiers',
  closable: true,
  editable: true,
  reorderable: true,
  tabType: 'Fee Tiers',
};

export const CUSTOMER_PRICING_TIERS_TAB_ITEMS: PersistedDefaultTabProps<CustomerPricingTabProps>[] = [
  CUSTOMER_PRICING_BASE_TIERS_TAB,
  CUSTOMER_PRICING_FEE_TIERS_TAB,
];

export const CUSTOMER_ALL_IN_TIERS_PERSIST_KEY = 'all-in-tiers';
export const CUSTOMER_FEE_TIERS_PERSIST_KEY = 'fee-tiers';
export const CUSTOMER_ALL_IN_TIERS_STANDALONE_PERSIST_KEY = `${CUSTOMER_ALL_IN_TIERS_PERSIST_KEY}/${CUSTOMER_PRICING_BASE_TIERS_TAB.id}`;
export const CUSTOMER_FEE_TIERS_STANDALONE_PERSIST_KEY = `${CUSTOMER_FEE_TIERS_PERSIST_KEY}/${CUSTOMER_PRICING_FEE_TIERS_TAB.id}`;

export const CUSTOMER_PRICING_TIERS_ALL_IN_TIERS_BULK_EDIT_FIELDS = [
  'BidSpread',
  'OfferSpread',
  'SalesCommission',
  'PricingAggregation',
  'Fee',
  'FeeMode',
  'FeeCurrency',
  'QuoteTTL',
  'AcceptPriceLeniency',
  'ExcludeExchangeFees',
] satisfies (keyof AllInTier)[];

export const CUSTOMER_PRICING_TIERS_FEE_TIERS_BULK_EDIT_FIELDS = [
  'Fee',
  'FeeMode',
  'FeeCurrency',
] satisfies (keyof FeeTier)[];
